import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../App';
import {
  useTicketLookup,
  fetchDuplicates,
} from '../hooks/useTicketLookups';
import EasychitReporterSection, {
  EASYCHIT_PRODUCT_ID,
  resolveFieldKey,
} from './EasychitReporterSection';
import SearchableSelect from '../../../components/SearchableSelect';
import { apiPath } from '../../../config/apiBase';

const REPORTED_BY_CANDIDATES = ['reported_by'];
const CONTACT_CANDIDATES = ['contact_no', 'contact', 'contact_number'];
const EMAIL_CANDIDATES = ['reporter_email', 'email'];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const CLIENT_FIELD_KEYS = new Set([
  'companies', 'company_name',
  'client_name', 'client', 'company', 'account_name',
  'company_or_office', 'client_or_company',
]);
const BRANCH_FIELD_KEYS = new Set([
  'branch', 'branch_name', 'office_branch', 'branch_or_office',
]);

function DependentSelect({
  label,
  name,
  value,
  onChange,
  options,
  loading,
  error,
  required,
  disabled,
  placeholder,
  optionValueKey = null,
  optionLabelKey = null,
}) {
  const isDisabled = disabled || loading;
  const getVal = (opt) => {
    if (optionValueKey && typeof opt === 'object' && opt !== null) return opt[optionValueKey];
    return opt;
  };
  const getLabel = (opt) => {
    if (optionLabelKey && typeof opt === 'object' && opt !== null) return opt[optionLabelKey];
    return opt;
  };

  const ssOptions = useMemo(
    () => (options || []).map((opt) => {
      const val = getVal(opt);
      const lbl = getLabel(opt);
      const notAccepting = typeof opt === 'object' && opt !== null && opt.accept_tickets === false;
      return {
        value: val,
        label: notAccepting ? `${String(lbl ?? val ?? '')} (not accepting tickets)` : String(lbl ?? val ?? ''),
        description: notAccepting ? 'Turned off in product management' : undefined,
        searchText: String(lbl ?? val ?? ''),
        disabled: notAccepting,
      };
    }),
    [options, optionValueKey, optionLabelKey],
  );

  return (
    <div>
      <label className="tk-label">
        {label}
        {required ? ' *' : ''}
        {loading ? ' (loading…)' : ''}
      </label>
      <SearchableSelect
        name={name}
        options={ssOptions}
        value={value || ''}
        onChange={(v) => onChange({ target: { name, value: v } })}
        placeholder={placeholder || 'Select…'}
        searchPlaceholder={`Search ${label.toLowerCase()}…`}
        disabled={isDisabled}
        required={required}
        aria-label={label}
      />
      {error && (
        <p style={{ fontSize: '12px', color: '#B91C1C', margin: '4px 0 0' }}>{error}</p>
      )}
    </div>
  );
}

function DynamicField({ field, value, onChange, productOptions = [], loadingProducts = false, bindProducts = false, dynamicOptions = null, dynamicLoading = false }) {
  const { field_key, label, field_type, required, options = [] } = field;
  const isDynamic = field.source_type === 'datasource';
  const optList = isDynamic
    ? (dynamicOptions || []).map((o) => ({ v: o.value, l: o.label }))
    : options.map((o) => ({ v: o, l: o }));

  if (bindProducts && field_key === 'product_name') {
    const productSs = (productOptions || []).map((p) => {
      const notAccepting = p.accept_tickets === false;
      return {
        value: p.product_id,
        label: notAccepting ? `${p.name} (not accepting tickets)` : p.name,
        description: notAccepting ? 'Turned off in product management' : undefined,
        searchText: `${p.name} ${p.product_id}`,
        disabled: notAccepting,
      };
    });
    return (
      <div>
        <label className="tk-label">
          Product{required ? ' *' : ''}
          {loadingProducts ? ' (loading…)' : ''}
        </label>
        <SearchableSelect
          name="product_id"
          options={productSs}
          value={value || ''}
          onChange={(v) => onChange({ target: { name: 'product_id', value: v } })}
          placeholder={loadingProducts ? 'Loading products…' : 'Select product'}
          searchPlaceholder="Search products…"
          disabled={loadingProducts}
          required={Boolean(required)}
          aria-label="Product"
        />
      </div>
    );
  }

  const common = {
    name: field_key,
    value: value || '',
    onChange,
    required: Boolean(required),
    placeholder: field.placeholder || undefined,
    className: 'tk-field',
  };
  const Help = () => (field.help_text
    ? <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '4px' }}>{field.help_text}</div>
    : null);
  const wrap = (inner) => (
    <div className={field.full_width ? 'tk-span-2' : undefined}>
      <label className="tk-label">{label}{required ? ' *' : ''}</label>
      {inner}
      <Help />
    </div>
  );

  if (field_type === 'textarea') {
    return wrap(<textarea {...common} rows={3} />);
  }

  if (field_type === 'select') {
    const dependsWaiting = isDynamic && field.depends_on && !optList.length && !dynamicLoading;
    const ssOptions = optList.map((opt) => ({
      value: opt.v,
      label: String(opt.l ?? opt.v ?? ''),
      searchText: String(opt.l ?? opt.v ?? ''),
    }));
    return wrap(
      <SearchableSelect
        name={field_key}
        options={ssOptions}
        value={value || ''}
        onChange={(v) => onChange({ target: { name: field_key, value: v } })}
        placeholder={dynamicLoading ? 'Loading…' : dependsWaiting ? 'Select the previous field first' : 'Select…'}
        searchPlaceholder={`Search ${label.toLowerCase()}…`}
        disabled={isDynamic && dynamicLoading}
        required={Boolean(required)}
        aria-label={label}
      />,
    );
  }

  if (field_type === 'radio') {
    return wrap(
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingTop: '4px' }}>
        {dynamicLoading && <span style={{ fontSize: '12px', color: '#6B7280' }}>Loading…</span>}
        {optList.map((opt) => (
          <label key={opt.v} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <input type="radio" name={field_key} value={opt.v}
              checked={value === opt.v} onChange={onChange} required={Boolean(required)} />
            {opt.l}
          </label>
        ))}
      </div>,
    );
  }

  if (field_type === 'checkbox') {
    if (!isDynamic && !options.length) {
      return wrap(
        <input type="checkbox" name={field_key} checked={value === true || value === 'true'}
          onChange={(e) => onChange({ target: { name: field_key, value: e.target.checked } })}
          style={{ width: '18px', height: '18px' }} />,
      );
    }
    const selected = Array.isArray(value) ? value : (value ? String(value).split(',') : []);
    const toggle = (v) => {
      const next = selected.includes(v) ? selected.filter((o) => o !== v) : [...selected, v];
      onChange({ target: { name: field_key, value: next.join(',') } });
    };
    return wrap(
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingTop: '4px' }}>
        {dynamicLoading && <span style={{ fontSize: '12px', color: '#6B7280' }}>Loading…</span>}
        {optList.map((opt) => (
          <label key={opt.v} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <input type="checkbox" checked={selected.includes(opt.v)} onChange={() => toggle(opt.v)} />
            {opt.l}
          </label>
        ))}
      </div>,
    );
  }

  const inputType = ['number', 'date', 'email'].includes(field_type) ? field_type : 'text';
  return wrap(<input type={inputType} {...common} />);
}

/**
 * Generic ITSM ticket form — Hardware, Software, and App Support domains.
 * mode="edit" reuses the same domain/product dropdowns for ticket updates.
 */
function DynamicTicketForm({
  form,
  onChange,
  onSubmit,
  submitting,
  error,
  lookupError,
  mode = 'create',
  onCancel,
  showAttachments = true,
}) {
  const isEdit = mode === 'edit';
  const [duplicates, setDuplicates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const { user } = useAuth();
  const role = user?.role === 'end_user' ? 'client' : user?.role;
  const isClient = role === 'client';
  const isRequestor = role === 'client' || role === 'agent';
  const [raiseScope, setRaiseScope] = useState(null);
  const appliedScopeRef = useRef('');

  // "Ticket Submitted By" is always the logged-in user on create. On edit, keep
  // the ticket's existing reporter values from the form (seeded by the detail page).
  useEffect(() => {
    if (!user || isEdit) return;
    const updates = {};
    if (user.name && form.reporter_name !== user.name) updates.reporter_name = user.name;
    if (user.email && form.reporter_email !== user.email) updates.reporter_email = user.email;
    if (Object.keys(updates).length) onChange(updates);
  }, [user, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isRequestor || isEdit) {
      setRaiseScope(null);
      return undefined;
    }
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios.get(apiPath('/api/users/me/ticket-scope'), {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!cancelled) setRaiseScope(res.data || { restricted: false, products: [] }); })
      .catch(() => { if (!cancelled) setRaiseScope({ restricted: false, products: [] }); });
    return () => { cancelled = true; };
  }, [isRequestor, isEdit]);

  const { options: domains, loading: loadingDomains, error: domainsError } =
    useTicketLookup('domains');
  const isProductDomain = form.domain === 'product';

  // Clients may only raise App Support (product) requests; Hardware/Software
  // are internal-only. Restrict their domain choices and default to App Support.
  const visibleDomains = useMemo(
    () => (isClient ? (domains || []).filter((d) => (d?.key ?? d) === 'product') : domains),
    [isClient, domains],
  );
  useEffect(() => {
    if (isClient && form.domain !== 'product') {
      onChange({ domain: 'product', category: '', service: '', domain_fields: {} });
    }
  }, [isClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Category & Service are no longer captured as fixed dropdowns for
  // Hardware/Software — those details now live in the customizable
  // domain-specific field definitions. Only App Support (product) still derives
  // category/service (from the chosen product) on the backend.
  const { options: fieldDefs, loading: loadingFields, error: fieldsError } =
    useTicketLookup('field-definitions', { domain: form.domain }, Boolean(form.domain));

  // Load active products for ANY domain: App Support uses them for the product
  // picker; Hardware/Software use them to resolve which product owns the
  // domain-specific form (the IT product).
  // For App Support create/edit, scope to products the user belongs to when they
  // are on at least one product team; non-members still see the full list.
  useEffect(() => {
    if (!form.domain) { setProducts([]); return; }
    let cancelled = false;
    setLoadingProducts(true);
    const token = localStorage.getItem('token');
    const params = form.domain === 'product' ? { member_scoped: true } : {};
    axios.get(apiPath('/api/products/active'), {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
      params,
    })
      .then((res) => {
        if (cancelled) return;
        const list = res.data || [];
        setProducts(list);
        // If current selection is not in the scoped list, or not accepting tickets, clear it.
        if (form.domain === 'product' && form.product_id && list.length) {
          const selected = list.find((p) => p.product_id === form.product_id);
          if (!selected || selected.accept_tickets === false) {
            onChange({ product_id: '' });
          }
        }
      })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoadingProducts(false); });
    return () => { cancelled = true; };
  }, [form.domain]); // eslint-disable-line react-hooks/exhaustive-deps

  // App Support picker shows only products that handle the 'product' domain.
  const productPickerOptions = useMemo(
    () => (products || []).filter((p) => (p.handles_domains || ['product']).includes('product')),
    [products],
  );

  const currentRaiseScope = useMemo(() => {
    if (!isRequestor || isEdit || !raiseScope?.restricted) return null;
    const list = raiseScope.products || [];
    if (form.product_id) {
      return list.find((p) => p.product_id === form.product_id) || null;
    }
    return list.length === 1 ? list[0] : null;
  }, [isRequestor, isEdit, raiseScope, form.product_id]);

  const lockSingleProduct = Boolean(
    isRequestor && !isEdit && raiseScope?.restricted && (raiseScope.products || []).length === 1,
  );

  useEffect(() => {
    if (!isRequestor || isEdit || !raiseScope?.restricted) return;
    if (!isClient && form.domain !== 'product') return;
    const list = raiseScope.products || [];
    if (list.length !== 1) return;
    const only = list[0];
    if (form.product_id === only.product_id) return;
    onChange({
      domain: 'product',
      product_id: only.product_id,
      category: only.name || '',
      service: 'General Support',
      domain_fields: {
        ...form.domain_fields,
        product_name: only.name || '',
      },
    });
  }, [isRequestor, isEdit, raiseScope]); // eslint-disable-line react-hooks/exhaustive-deps

  // The product whose active template drives the domain-specific section:
  //   App Support      -> the product the requester picked
  //   Hardware/Software -> the product configured to handle that domain (IT).
  // Prefer a dedicated internal handler (one that doesn't also do App Support).
  const templateProductId = useMemo(() => {
    if (isProductDomain) return form.product_id || null;
    const handlers = (products || []).filter(
      (p) => (p.handles_domains || []).includes(form.domain));
    const dedicated = handlers.find((p) => !(p.handles_domains || []).includes('product'));
    return (dedicated || handlers[0])?.product_id || null;
  }, [isProductDomain, form.product_id, products, form.domain]);

  // Load the active custom form template for the resolved product.
  // templateFields: null = not loaded, [] = no template (use generic fields).
  const [templateFields, setTemplateFields] = useState(null);
  useEffect(() => {
    if (!templateProductId) { setTemplateFields(null); return; }
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios.get(`${apiPath('/api/tickets/form-templates/active')}?product_id=${templateProductId}`, {
      withCredentials: true, headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!cancelled) setTemplateFields(res.data ? (res.data.fields || []) : []); })
      .catch(() => { if (!cancelled) setTemplateFields([]); });
    return () => { cancelled = true; };
  }, [templateProductId]);

  // The resolved product's active template drives the domain-specific fields;
  // fall back to the seeded field-definitions when no template exists yet.
  const effectiveFieldDefs = (templateProductId && templateFields && templateFields.length)
    ? templateFields
    : fieldDefs;

  const isEasychitProduct = templateProductId === EASYCHIT_PRODUCT_ID
    || products.some((p) => p.product_id === templateProductId && p.product_key === 'easychit');
  const reporterKeysToSkip = useMemo(() => {
    if (!isEasychitProduct) return new Set();
    return new Set([
      resolveFieldKey(effectiveFieldDefs, REPORTED_BY_CANDIDATES),
      resolveFieldKey(effectiveFieldDefs, CONTACT_CANDIDATES),
      resolveFieldKey(effectiveFieldDefs, EMAIL_CANDIDATES),
    ].filter(Boolean));
  }, [isEasychitProduct, effectiveFieldDefs]);

  const lockedFieldKeys = useMemo(() => {
    const keys = new Set();
    if (!isRequestor || isEdit || !currentRaiseScope || currentRaiseScope.clients_unrestricted) {
      return keys;
    }
    const clients = currentRaiseScope.clients || [];
    if (clients.length === 1) {
      CLIENT_FIELD_KEYS.forEach((k) => keys.add(k));
      if (clients[0].parent_field) keys.add(clients[0].parent_field);
      const branches = clients[0].branches || [];
      if (!clients[0].branches_unrestricted && branches.length === 1) {
        BRANCH_FIELD_KEYS.forEach((k) => keys.add(k));
      }
    }
    return keys;
  }, [isRequestor, isEdit, currentRaiseScope]);

  useEffect(() => {
    if (!isRequestor || isEdit || !currentRaiseScope || currentRaiseScope.clients_unrestricted) return;
    const defs = effectiveFieldDefs || [];
    if (!defs.length) return;
    const clients = currentRaiseScope.clients || [];
    const next = { ...(form.domain_fields || {}) };
    let changed = false;
    const applyKeys = (keySet, value) => {
      if (!value) return;
      defs.forEach((f) => {
        if (keySet.has(f.field_key) && next[f.field_key] !== value) {
          next[f.field_key] = value;
          changed = true;
        }
      });
    };
    if (clients.length === 1) {
      const c = clients[0];
      applyKeys(CLIENT_FIELD_KEYS, c.name);
      if (c.parent_field && c.parent_value && next[c.parent_field] !== c.parent_value) {
        next[c.parent_field] = c.parent_value;
        changed = true;
      }
      if (!c.branches_unrestricted && (c.branches || []).length === 1) {
        applyKeys(BRANCH_FIELD_KEYS, c.branches[0]);
      }
    } else if (clients.length > 1) {
      const parents = [...new Set(clients.map((c) => c.parent_value).filter(Boolean))];
      const parentField = clients.find((c) => c.parent_field)?.parent_field;
      if (parentField && parents.length === 1 && next[parentField] !== parents[0]) {
        next[parentField] = parents[0];
        changed = true;
      }
    }
    const sig = `${form.product_id}|${JSON.stringify(next)}`;
    if (changed && appliedScopeRef.current !== sig) {
      appliedScopeRef.current = sig;
      onChange({ domain_fields: next });
    }
  }, [isRequestor, isEdit, currentRaiseScope, effectiveFieldDefs, form.product_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve options for data-source-backed fields (generic dynamic/cascading
  // dropdowns). Options live in dsOptions[field_key]; a parent value change
  // re-resolves the child. resolvedRef prevents redundant refetches.
  const [dsOptions, setDsOptions] = useState({});
  const [dsLoading, setDsLoading] = useState({});
  const resolvedRef = useRef({});
  useEffect(() => {
    const dsFields = (effectiveFieldDefs || []).filter(
      (f) => f.source_type === 'datasource' && f.data_source_id);
    if (!dsFields.length) return;
    const token = localStorage.getItem('token');
    dsFields.forEach((f) => {
      const params = {};
      if (f.depends_on) {
        const pv = form.domain_fields?.[f.depends_on];
        if (!pv) {
          // Parent not chosen yet → no options; clear any stale set once.
          if (resolvedRef.current[f.field_key] !== '__empty__') {
            resolvedRef.current[f.field_key] = '__empty__';
            setDsOptions((o) => ({ ...o, [f.field_key]: [] }));
          }
          return;
        }
        params[f.depends_on] = pv;
      }
      const sig = `${f.data_source_id}|${JSON.stringify(params)}`;
      if (resolvedRef.current[f.field_key] === sig) return; // already resolved
      resolvedRef.current[f.field_key] = sig;
      setDsLoading((l) => ({ ...l, [f.field_key]: true }));
      axios.post(apiPath('/api/tickets/data-sources/resolve'),
        { data_source_id: f.data_source_id, params },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setDsOptions((o) => ({ ...o, [f.field_key]: res.data || [] })))
        .catch(() => setDsOptions((o) => ({ ...o, [f.field_key]: [] })))
        .finally(() => setDsLoading((l) => ({ ...l, [f.field_key]: false })));
    });
  }, [effectiveFieldDefs, form.domain_fields]);

  const scopedDsOptions = useMemo(() => {
    if (!currentRaiseScope || currentRaiseScope.clients_unrestricted) return dsOptions;
    const allowedClients = new Set(
      (currentRaiseScope.clients || []).map((c) => String(c.name || '').toLowerCase()),
    );
    const selectedClient = Object.entries(form.domain_fields || {}).reduce((found, [key, val]) => {
      if (found) return found;
      if (CLIENT_FIELD_KEYS.has(key) && val) return String(val);
      return found;
    }, '');
    const clientRow = (currentRaiseScope.clients || []).find(
      (c) => String(c.name || '').toLowerCase() === selectedClient.toLowerCase(),
    );
    const next = { ...dsOptions };
    (effectiveFieldDefs || []).forEach((f) => {
      const opts = dsOptions[f.field_key] || [];
      if (CLIENT_FIELD_KEYS.has(f.field_key) && allowedClients.size) {
        next[f.field_key] = opts.filter((o) => allowedClients.has(String(o.value || '').toLowerCase()));
      } else if (BRANCH_FIELD_KEYS.has(f.field_key) && clientRow && !clientRow.branches_unrestricted) {
        const allowedBr = new Set((clientRow.branches || []).map((b) => String(b).toLowerCase()));
        next[f.field_key] = opts.filter((o) => allowedBr.has(String(o.value || '').toLowerCase()));
      }
    });
    return next;
  }, [dsOptions, currentRaiseScope, effectiveFieldDefs, form.domain_fields]);

  useEffect(() => {
    if (isEdit) {
      setDuplicates([]);
      return undefined;
    }
    const canCheckHardwareSoftware = !isProductDomain && form.domain && form.service;
    const canCheckProduct = isProductDomain && form.product_id;
    if (!canCheckHardwareSoftware && !canCheckProduct) {
      setDuplicates([]);
      return undefined;
    }

    let cancelled = false;
    const params = {
      domain: form.domain,
      reporter_email: form.reporter_email || undefined,
      reporter_name: form.reporter_name || undefined,
    };
    if (isProductDomain) {
      params.product_id = form.product_id;
      params.service = form.service || 'General Support';
    } else {
      params.service = form.service;
    }

    fetchDuplicates(params)
      .then((rows) => { if (!cancelled) setDuplicates(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setDuplicates([]); });

    return () => { cancelled = true; };
  }, [form.domain, form.service, form.product_id, form.reporter_email, form.reporter_name, isProductDomain, isEdit]);

  const applyProductSelection = (productId) => {
    const product = products.find((p) => p.product_id === productId);
    onChange({
      product_id: productId,
      category: product?.ticket_category || product?.name || '',
      service: 'General Support',
      domain_fields: {
        ...form.domain_fields,
        product_name: product?.name || '',
      },
    });
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === 'product_id') {
      applyProductSelection(value);
      return;
    }
    if (name.startsWith('domain_field_')) {
      const key = name.replace('domain_field_', '');
      const nextFields = { ...form.domain_fields, [key]: value };
      // Cascade reset: clear any (transitive) children that depend on this field
      // so a stale child value can't be submitted against a changed parent.
      const toClear = [key];
      while (toClear.length) {
        const k = toClear.pop();
        (effectiveFieldDefs || []).forEach((f) => {
          if (f.depends_on === k && nextFields[f.field_key]) {
            nextFields[f.field_key] = '';
            toClear.push(f.field_key);
          }
        });
      }
      onChange({ domain_fields: nextFields });
      return;
    }

    const updates = { [name]: value };
    if (name === 'domain') {
      // Persist domain as category for HW/SW (no separate category picker).
      if (value === 'hardware' || value === 'software') {
        updates.category = value.charAt(0).toUpperCase() + value.slice(1);
      } else {
        updates.category = '';
      }
      updates.service = '';
      updates.product_id = '';
      updates.domain_fields = {};
    }
    onChange(updates);
  };

  const combinedLookupError = lookupError || domainsError || fieldsError;

  return (
    <form onSubmit={onSubmit} className="tk-form-grid">
      {combinedLookupError && (
        <div className="tk-alert tk-alert-warning tk-span-2">
          Lookup error: {combinedLookupError}. Refresh or contact an admin if dropdowns stay empty.
        </div>
      )}

      {duplicates.length > 0 && (
        <div className="tk-alert tk-alert-warning tk-span-2">
          <strong>{duplicates.length} existing open ticket(s)</strong> for this request type:{' '}
          {duplicates.map((t) => t.ticket_no).join(', ')}
        </div>
      )}

      {error && (
        <div className="tk-alert tk-alert-error tk-span-2">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      {isRequestor && !isEdit && raiseScope?.restricted && (raiseScope.products || []).length === 0 && (
        <div className="tk-alert tk-alert-warning tk-span-2">
          You do not have product access yet. Ask an admin to assign products on the Users page.
        </div>
      )}

      <DependentSelect
        label="Domain"
        name="domain"
        value={form.domain}
        onChange={handleFieldChange}
        options={visibleDomains}
        loading={loadingDomains}
        error={domainsError}
        required
        disabled={isClient}
        placeholder="Select domain"
        optionValueKey="key"
        optionLabelKey="label"
      />
      <div>
        <label className="tk-label">Priority</label>
        <SearchableSelect
          name="priority"
          options={PRIORITY_OPTIONS}
          value={form.priority}
          onChange={(v) => handleFieldChange({ target: { name: 'priority', value: v } })}
          placeholder="Select priority…"
          searchPlaceholder="Filter priority…"
          aria-label="Priority"
        />
      </div>
      {form.domain && !loadingFields && (effectiveFieldDefs.length > 0 || isProductDomain) && (
        <>
          <div className="tk-span-2" style={{ marginTop: '4px' }}>
            <h3 className="tk-section-title" style={{ marginBottom: 0 }}>
              Domain-specific Details
            </h3>
          </div>
          {isProductDomain && !lockSingleProduct && !effectiveFieldDefs.some((f) => f.field_key === 'product_name') && (
            <DependentSelect
              label="Product"
              name="product_id"
              value={form.product_id || ''}
              onChange={handleFieldChange}
              options={productPickerOptions}
              loading={loadingProducts}
              required
              placeholder={loadingProducts ? 'Loading products…' : productPickerOptions.length ? 'Select product' : 'No products configured'}
              optionValueKey="product_id"
              optionLabelKey="name"
            />
          )}
          {effectiveFieldDefs.map((field) => {
            const isProductPicker = isProductDomain && field.field_key === 'product_name';
            if (reporterKeysToSkip.has(field.field_key)) return null;
            if (lockedFieldKeys.has(field.field_key)) return null;
            if (lockSingleProduct && isProductPicker) return null;
            return (
              <DynamicField
                key={field.field_key}
                field={isProductPicker ? { ...field, field_type: 'select', label: 'Product' } : field}
                value={isProductPicker ? (form.product_id || '') : (form.domain_fields?.[field.field_key] || '')}
                productOptions={productPickerOptions}
                loadingProducts={loadingProducts}
                bindProducts={isProductPicker}
                dynamicOptions={scopedDsOptions[field.field_key] || []}
                dynamicLoading={Boolean(dsLoading[field.field_key])}
                onChange={isProductPicker
                  ? (e) => applyProductSelection(e.target.value)
                  : (e) => {
                    handleFieldChange({
                      target: { name: `domain_field_${field.field_key}`, value: e.target.value },
                    });
                  }}
              />
            );
          })}
          {isEasychitProduct && (
            <EasychitReporterSection
              fieldDefs={effectiveFieldDefs}
              domainFields={form.domain_fields || {}}
              productId={templateProductId}
              onChange={onChange}
            />
          )}
        </>
      )}

      <div className="tk-span-2">
        <label className="tk-label">Subject *</label>
        <input name="subject" value={form.subject} onChange={handleFieldChange} required className="tk-field" />
      </div>
      <div className="tk-span-2">
        <label className="tk-label">Description *</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleFieldChange}
          required
          rows={4}
          className="tk-field"
        />
      </div>

      <div className="tk-span-2" style={{ marginTop: '4px' }}>
        <label className="tk-label">Ticket Submitted By</label>
        <div className="tk-field-readonly">
          {isEdit
            ? (form.reporter_name || user?.name || 'Current user')
            : (user?.name || 'Current user')}
          {(isEdit ? form.reporter_email : user?.email)
            ? ` (${isEdit ? form.reporter_email : user?.email})`
            : ''}
        </div>
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94A3B8' }}>
          {isEdit
            ? 'Submitter stays as originally logged; change domain fields below for reported-by details.'
            : 'This ticket is logged under your account.'}
        </p>
      </div>

      {showAttachments && (
      <div className="tk-span-2" style={{ marginTop: '4px' }}>
        <label className="tk-label">Attachments / Screenshots</label>
        <input
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt,.log,.csv,.xls,.xlsx"
          onChange={(e) => {
            const picked = Array.from(e.target.files || []);
            onChange({ _attachments: [...(form._attachments || []), ...picked] });
            e.target.value = '';
          }}
          style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}
        />
        <p style={{ fontSize: '12px', color: '#94A3B8', margin: '4px 0 0' }}>
          Attach screenshots or reference files (images, PDF, docs). They upload after the ticket is created.
        </p>
        {(form._attachments || []).length > 0 && (
          <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
            {(form._attachments || []).map((f, idx) => (
              <li key={`${f.name}-${idx}`} style={{ fontSize: '12px', color: '#0F172A' }}>
                {f.name} <span style={{ color: '#94A3B8' }}>({Math.round(f.size / 1024)} KB)</span>{' '}
                <button
                  type="button"
                  onClick={() => onChange({ _attachments: form._attachments.filter((_, j) => j !== idx) })}
                  className="tk-btn tk-btn-ghost"
                  style={{ padding: '0 4px', color: '#DC2626' }}
                >
                  remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      <div className="tk-span-2" style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          type="submit"
          disabled={submitting}
          className="tk-btn tk-btn-primary tk-cta"
        >
          {submitting
            ? (isEdit ? 'Saving…' : 'Creating…')
            : (isEdit ? 'Save changes' : 'Create Ticket')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="tk-btn tk-btn-secondary" disabled={submitting}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default DynamicTicketForm;
